import { Directive, effect, inject, Input, OnDestroy, Renderer2, TemplateRef, untracked, ViewContainerRef } from '@angular/core';
import { AUTH_SESSION } from '@shared/di/auth-session.token';
import { AuthSession } from '@shared/interfaces/auth-session.interface';
import { LoggerService } from '../services/logger.service';
import { AuthOptions } from '../models/auth-options.model';
import { AbstractAuthDirective } from './abstract-auth.directive';
import { EnAuthOptionsOperator } from '../enums/auth-options-operator.enum';

/**
 * Directive that manages authorization and conditional display of DOM elements
 * based on current user authority or permissions.
 *
 * Usage:
 * <div [dataAuth]="{ id: 'PERMISSION_NAME || AUTHORITY_NAME', klass: 'custom-class' }">Protected content</div>
 */
@Directive({
  selector: '[data-roles], [data-permissions]',
  standalone: true,
})
export class AuthDirective extends AbstractAuthDirective implements OnDestroy {
  protected override readonly loggerService: LoggerService;
  protected override readonly viewContainerRef: ViewContainerRef;
  protected override readonly templateRef: TemplateRef<unknown>;
  protected override readonly renderer2: Renderer2;
  protected override readonly authService: AuthSession;

  private hasView = false;
  private internalCachedOptions: AuthOptions | null = null;

  /**
   * Whether a value has ever been processed. Distinguishes "no options yet" from
   * "options that happen to be null", which the cached value alone cannot.
   */
  private initialized = false;
  private authKind: 'roles' | 'permissions' | undefined;
  /**
   * The bound instance, kept apart from `internalCachedOptions`. That one is a
   * `{...options}` copy for change detection: spreading an AuthOptions drops its
   * prototype getters (`id`, `operator`, `exact`), so a gate evaluated on the copy
   * sees no id, denies, and hides content the user is allowed to see.
   */
  private boundOptions: AuthOptions | null = null;

  // Add support for different auth types
  @Input({ alias: 'data-roles' }) set dataRoles(options: AuthOptions | null) {
    this.loggerService.debug(this.constructor.name, 'Data roles', options);
    this.authKind = 'roles';
    this.processAuthChange(options, 'roles');
  }

  @Input({ alias: 'data-permissions' }) set dataPermissions(options: AuthOptions | null) {
    this.loggerService.debug(this.constructor.name, 'Data permissions', options);
    this.authKind = 'permissions';
    this.processAuthChange(options, 'permissions');
  }

  private processAuthChange(options: AuthOptions | null, type?: 'roles' | 'permissions') {
    // The "unchanged, skip the work" shortcut applies from the SECOND value on.
    //
    // `internalCachedOptions` starts as null and `compare` reports two nulls as
    // equal, so a binding whose FIRST value was null returned here before
    // `showContent()` had ever run and the element never rendered — contradicting
    // the `if (!options) showContent()` rule right below. It stayed hidden because
    // "absent" and "explicitly null" took different paths: an unbound input
    // arrives as `undefined`, which does not compare equal to null and therefore
    // fell through to that rule.
    //
    // Keyed on `initialized` rather than on `hasView`: a hidden element has no
    // view either, so that test would re-run the check on every emission for
    // exactly the elements that are denied — and a caller binding a fresh object
    // literal emits on every change-detection pass.
    if (this.initialized && this.compare(this.internalCachedOptions, options)) return;
    this.initialized = true;
    this.internalCachedOptions = options ? ({...options} as AuthOptions) : null;
    this.boundOptions = options;
    if (!options) {
      this.showContent();
      return;
    }
    if (!this.validateOptions(options)) {
      this.hideContent();
      return;
    }
    this.processAuthorization(options, type);
  }

  protected processAuthorization(options: AuthOptions, type?: string): void {
    if (this.passesGate(options, type) && this.applyOptions(options)) {
      this.showContent();
    } else {
      this.hideContent();
    }
  }

  /**
   * Session signals changed. Re-run the gate only: processAuthorization also
   * calls applyOptions, which creates a detached view and runs options.callback.
   */
  private recheckGate(): void {
    const options = this.boundOptions;
    if (!this.initialized || !options?.id) return;
    if (this.passesGate(options, this.authKind)) {
      this.showContent();
    } else {
      this.hideContent();
    }
  }

  private passesGate(options: AuthOptions, type?: string): boolean {
    switch (type) {
      case 'roles':
        return this.checkAuthority(options);
      case 'permissions':
        return this.checkPermission(options);
      default: {
        const hasAuthority = this.checkAuthority(options);
        const hasPermission = this.checkPermission(options);
        return options.operator === EnAuthOptionsOperator.OR
          ? (hasAuthority || hasPermission)
          : (hasAuthority && hasPermission);
      }
    }
  }

  constructor() {
    const loggerService = inject(LoggerService);
    const viewContainerRef = inject(ViewContainerRef);
    const templateRef = inject<TemplateRef<unknown>>(TemplateRef);
    const renderer2 = inject(Renderer2);
    const authService = inject(AUTH_SESSION);

    super(loggerService, viewContainerRef, templateRef, renderer2, authService);

    this.loggerService = loggerService;
    this.viewContainerRef = viewContainerRef;
    this.templateRef = templateRef;
    this.renderer2 = renderer2;
    this.authService = authService;

    // Input setters are not a reactive context. Reading the session signals here
    // re-checks the gate when permissions arrive after the first check.
    effect(() => {
      this.authService.permissionNames();
      this.authService.authorityNames();
      untracked(() => this.recheckGate());
    });
  }

  /**
   * Cleans up resources when the directive is destroyed
   * Can be overridden by child classes
   */
  ngOnDestroy(): void {
    this.clearView();
    this.internalCachedOptions = null;
    this.boundOptions = null;
    // Reset in step with the cached value: leaving one of the pair behind is the
    // same asymmetry that hid the null case in the first place.
    this.initialized = false;
  }

  /*
   * Shows the content
   */
  private showContent(): void {
    if (!this.hasView) {
      this.viewContainerRef.clear();
      this.viewContainerRef.createEmbeddedView(this.templateRef);
      this.hasView = true;
    }
  }

  /*
   * Hides the content
   */
  private hideContent(): void {
    if (this.hasView) {
      this.viewContainerRef.clear();
      this.hasView = false;
    }
  }

  /*
   * Compare two AuthOptions objects for equality
   * @param opt1 First AuthOptions object
   * @param opt2 Second AuthOptions object
   * @returns true if the options are equivalent
   */
  private compare(opt1: AuthOptions | null, opt2: AuthOptions | null): boolean {
    if (opt1 === opt2) return true;
    if (!opt1 || !opt2) return false;
    return JSON.stringify(opt1) === JSON.stringify(opt2);
  }
}
